import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, ClipboardCheck, Loader2, ShieldCheck } from "lucide-react";

const RESULTS = [
  { value: "wirksam", label: "Wirksam" },
  { value: "teilweise_wirksam", label: "Teilweise wirksam" },
  { value: "nicht_wirksam", label: "Nicht wirksam" },
];
const METHODS = [
  { value: "beobachtung", label: "Beobachtung" },
  { value: "befragung", label: "Befragung" },
  { value: "praktische_vorfuehrung", label: "Praktische Vorführung" },
];
const FOLLOW_UPS = [
  "Nachunterweisung erforderlich",
  "Gespräch geführt",
  "Arbeitsmittel angepasst",
  "Sonstiges",
];
// P2.1 – Wiedervorlage-Optionen. Standard 30 Tage; keine risikobasierte Vorauswahl.
const FOLLOW_UP_INTERVALS = [
  { value: "14", label: "14 Tage" },
  { value: "30", label: "30 Tage" },
  { value: "60", label: "60 Tage" },
  { value: "90", label: "90 Tage" },
  { value: "custom", label: "Individuelles Datum" },
];
const NEEDS_FOLLOW_UP = new Set(["teilweise_wirksam", "nicht_wirksam"]);

function formatDate(iso) {
  if (!iso) return "–";
  try {
    return new Date(iso).toLocaleDateString("de-DE", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  } catch {
    return iso;
  }
}

// Spiegelt das serverseitige Fälligkeits-Gate für die UX: vor `due_at` ist keine
// Bestätigung möglich. Die eigentliche Durchsetzung erfolgt serverseitig.
function isBeforeDue(iso) {
  if (!iso) return false;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return false;
  return Date.now() < d.getTime();
}

/**
 * Prüfmaske als Dialog (Schritt 4). Listet offene Wirksamkeitskontrollen und
 * lässt den Unternehmer eine bestätigen (in < 30 Sekunden). CLartext bewertet
 * nichts – der Klick dokumentiert eine tatsächlich durchgeführte Beobachtung.
 */
export default function WirksamkeitDialog({ open, onOpenChange, defaultName = "", onConfirmed }) {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);

  // Formularzustand der Prüfmaske.
  const [name, setName] = useState("");
  const [result, setResult] = useState("");
  const [method, setMethod] = useState("beobachtung");
  const [note, setNote] = useState("");
  const [followUpChoice, setFollowUpChoice] = useState("");
  const [followUpCustom, setFollowUpCustom] = useState("");
  // P2.1 – Wiedervorlage (nur bei Abweichung).
  const [followUpInterval, setFollowUpInterval] = useState("30");
  const [followUpDueDate, setFollowUpDueDate] = useState("");
  const [saving, setSaving] = useState(false);

  const loadList = async () => {
    setLoading(true);
    try {
      setList(await api.listOpenWirksamkeit());
    } catch {
      toast.error("Offene Wirksamkeitskontrollen konnten nicht geladen werden.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      setSelected(null);
      loadList();
    }
  }, [open]);

  const openDetail = (item) => {
    setSelected(item);
    setName(defaultName || "");
    setResult("");
    setMethod("beobachtung");
    setNote("");
    setFollowUpChoice("");
    setFollowUpCustom("");
    setFollowUpInterval("30");
    setFollowUpDueDate("");
  };

  const followUpValue = useMemo(() => {
    if (followUpChoice === "Sonstiges") return followUpCustom.trim();
    return followUpChoice;
  }, [followUpChoice, followUpCustom]);

  const followUpRequired = NEEDS_FOLLOW_UP.has(result);

  // Vor Fälligkeit: ansehen erlaubt, Bestätigen gesperrt (UX-Spiegel des Server-Gates).
  const notDue = selected ? isBeforeDue(selected.due_at) : false;

  // P2.1 – berechnetes Wiedervorlage-Datum (ISO). 14/30/60/90 = heute + N Tage;
  // "custom" = gewähltes Datum (09:00). Teil des Beweises (geht in den Hash ein).
  const followUpDueAt = useMemo(() => {
    if (!followUpRequired) return "";
    if (followUpInterval === "custom") {
      if (!followUpDueDate) return "";
      const d = new Date(`${followUpDueDate}T09:00:00`);
      return isNaN(d.getTime()) ? "" : d.toISOString();
    }
    const days = parseInt(followUpInterval, 10);
    const d = new Date();
    d.setHours(9, 0, 0, 0);
    d.setDate(d.getDate() + days);
    return d.toISOString();
  }, [followUpRequired, followUpInterval, followUpDueDate]);

  const canSubmit =
    !notDue &&
    name.trim() &&
    result &&
    method &&
    (!followUpRequired || (followUpValue && followUpDueAt));

  const submit = async () => {
    if (!canSubmit || saving) return;
    setSaving(true);
    try {
      await api.confirmWirksamkeit(selected.id, {
        checked_by_name: name.trim(),
        result,
        method,
        note: note.trim(),
        follow_up_measure: followUpRequired ? followUpValue : "",
        follow_up_due_at: followUpRequired ? followUpDueAt : undefined,
        follow_up_interval_choice: followUpRequired ? followUpInterval : undefined,
      });
      toast.success("Wirksamkeitskontrolle bestätigt.");
      setSelected(null);
      await loadList();
      onConfirmed?.();
    } catch (e) {
      const d = e?.response?.data?.detail;
      const msg = (typeof d === "string" ? d : d?.message) || "Bestätigung fehlgeschlagen.";
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="wirksamkeit-dialog">
        {!selected ? (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <ShieldCheck className="w-5 h-5 text-slate-700" />
                Offene Wirksamkeitskontrollen
              </DialogTitle>
              <DialogDescription>
                Prüfen Sie, ob die Schutzmaßnahmen im Arbeitsalltag eingehalten werden.
              </DialogDescription>
            </DialogHeader>

            {loading ? (
              <div className="py-12 flex justify-center text-slate-400">
                <Loader2 className="w-6 h-6 animate-spin" />
              </div>
            ) : list.length === 0 ? (
              <div
                className="py-12 text-center text-slate-500 text-sm"
                data-testid="wirksamkeit-empty"
              >
                Keine offenen Wirksamkeitskontrollen. Alles erledigt.
              </div>
            ) : (
              <div className="divide-y divide-slate-100 max-h-[60vh] overflow-y-auto -mx-1">
                {list.map((item) => (
                  <button
                    key={item.id}
                    onClick={() => openDetail(item)}
                    data-testid={`wirksamkeit-item-${item.id}`}
                    className="w-full text-left px-4 py-4 hover:bg-slate-50 transition-colors flex items-center justify-between gap-3"
                  >
                    <div className="min-w-0">
                      <div className="font-medium text-slate-900 truncate">
                        {item.welt_title || "Welt"}
                      </div>
                      <div className="text-xs text-slate-500 mt-0.5">
                        Fällig: {formatDate(item.due_at)}
                      </div>
                    </div>
                    {item.overdue ? (
                      // WK-02, Betreiberfreigabe 18.10. (W2): einheitliche Wortwahl.
                      <Badge className="bg-red-100 text-red-700 hover:bg-red-100">
                        Zur Prüfung fällig
                      </Badge>
                    ) : (
                      <Badge variant="secondary">Offen</Badge>
                    )}
                  </button>
                ))}
              </div>
            )}
          </>
        ) : (
          <>
            <DialogHeader>
              <button
                onClick={() => setSelected(null)}
                data-testid="wirksamkeit-back"
                className="text-sm text-slate-500 hover:text-slate-800 inline-flex items-center gap-1 mb-1"
              >
                <ArrowLeft className="w-4 h-4" /> Zurück zur Liste
              </button>
              <DialogTitle className="flex items-center gap-2">
                <ClipboardCheck className="w-5 h-5 text-slate-700" />
                {selected.welt_title || "Welt"}
              </DialogTitle>
              <DialogDescription>Fällig: {formatDate(selected.due_at)}</DialogDescription>
            </DialogHeader>

            <div className="space-y-5 max-h-[65vh] overflow-y-auto pr-1">
              <div className="rounded-md bg-slate-50 border border-slate-200 p-4">
                <p className="text-sm text-slate-600">
                  Bitte beobachten Sie die Umsetzung der folgenden Schutzmaßnahmen im
                  Arbeitsalltag und dokumentieren Sie Ihre Einschätzung.
                </p>
                <ul
                  className="mt-3 space-y-1.5 list-disc list-inside text-sm text-slate-800"
                  data-testid="wirksamkeit-observation-points"
                >
                  {(selected.observation_points || []).map((p, i) => (
                    <li key={i}>{p}</li>
                  ))}
                </ul>
              </div>

              <div>
                <Label className="text-sm font-medium text-slate-900">
                  Werden die folgenden Schutzmaßnahmen im Arbeitsalltag eingehalten?
                </Label>
                <RadioGroup
                  value={result}
                  onValueChange={setResult}
                  className="mt-3 grid gap-2"
                  data-testid="wirksamkeit-result"
                >
                  {RESULTS.map((r) => (
                    <label
                      key={r.value}
                      htmlFor={`res-${r.value}`}
                      className="flex items-center gap-3 rounded-md border border-slate-200 px-3 py-2.5 cursor-pointer hover:bg-slate-50"
                    >
                      <RadioGroupItem
                        value={r.value}
                        id={`res-${r.value}`}
                        data-testid={`wirksamkeit-result-${r.value}`}
                      />
                      <span className="text-sm text-slate-800">{r.label}</span>
                    </label>
                  ))}
                </RadioGroup>
              </div>

              <div>
                <Label className="text-sm font-medium text-slate-900">Prüfmethode</Label>
                <Select value={method} onValueChange={setMethod}>
                  <SelectTrigger className="mt-2" data-testid="wirksamkeit-method">
                    <SelectValue placeholder="Methode wählen" />
                  </SelectTrigger>
                  <SelectContent>
                    {METHODS.map((m) => (
                      <SelectItem key={m.value} value={m.value}>
                        {m.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {followUpRequired && (
                <div data-testid="wirksamkeit-followup-block">
                  <Label className="text-sm font-medium text-slate-900">
                    Eingeleitete Maßnahme <span className="text-red-600">*</span>
                  </Label>
                  <Select value={followUpChoice} onValueChange={setFollowUpChoice}>
                    <SelectTrigger className="mt-2" data-testid="wirksamkeit-followup">
                      <SelectValue placeholder="Maßnahme wählen" />
                    </SelectTrigger>
                    <SelectContent>
                      {FOLLOW_UPS.map((f) => (
                        <SelectItem key={f} value={f}>
                          {f}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {followUpChoice === "Sonstiges" && (
                    <Input
                      className="mt-2"
                      placeholder="Bitte beschreiben"
                      value={followUpCustom}
                      onChange={(e) => setFollowUpCustom(e.target.value)}
                      data-testid="wirksamkeit-followup-custom"
                    />
                  )}

                  <div className="mt-4">
                    <Label className="text-sm font-medium text-slate-900">
                      Wiedervorlage <span className="text-red-600">*</span>
                    </Label>
                    <p className="text-xs text-slate-500 mt-0.5">
                      Frist legen Sie als verantwortliche Person aktiv fest.
                    </p>
                    <Select value={followUpInterval} onValueChange={setFollowUpInterval}>
                      <SelectTrigger className="mt-2" data-testid="wirksamkeit-followup-interval">
                        <SelectValue placeholder="Frist wählen" />
                      </SelectTrigger>
                      <SelectContent>
                        {FOLLOW_UP_INTERVALS.map((iv) => (
                          <SelectItem key={iv.value} value={iv.value}>
                            {iv.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {followUpInterval === "custom" ? (
                      <Input
                        type="date"
                        className="mt-2"
                        min={new Date().toISOString().slice(0, 10)}
                        value={followUpDueDate}
                        onChange={(e) => setFollowUpDueDate(e.target.value)}
                        data-testid="wirksamkeit-followup-custom-date"
                      />
                    ) : null}
                    {followUpDueAt && (
                      <p
                        className="text-xs text-slate-600 mt-2"
                        data-testid="wirksamkeit-followup-due-preview"
                      >
                        Nachkontrolle fällig am {formatDate(followUpDueAt)}
                      </p>
                    )}
                  </div>
                </div>
              )}

              <div>
                <Label className="text-sm font-medium text-slate-900">
                  Bemerkung <span className="text-slate-400 font-normal">(optional)</span>
                </Label>
                <Textarea
                  className="mt-2"
                  rows={2}
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  data-testid="wirksamkeit-note"
                />
              </div>

              <div>
                <Label className="text-sm font-medium text-slate-900">
                  Bestätigt durch <span className="text-red-600">*</span>
                </Label>
                <Input
                  className="mt-2"
                  placeholder="Name der bestätigenden Person"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  data-testid="wirksamkeit-checked-by"
                />
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
              {notDue && (
                <p
                  className="text-xs text-amber-700 mr-auto self-center max-w-[60%]"
                  data-testid="wirksamkeit-not-due-hint"
                >
                  Bestätigung möglich ab {formatDate(selected.due_at)} – nach Beobachtung im Betrieb.
                </p>
              )}
              <Button variant="outline" onClick={() => setSelected(null)} disabled={saving}>
                Abbrechen
              </Button>
              <Button
                onClick={submit}
                disabled={!canSubmit || saving}
                data-testid="wirksamkeit-confirm-submit"
              >
                {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Bestätigen
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
